import { useEffect, useMemo, useState, memo, useCallback, useRef } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import parseInt from 'lodash/parseInt';
import PropTypes from 'prop-types';
import { Form } from 'semantic-ui-react';
import { Container } from '@mui/material';
import Typography from '@mui/material/Typography';
import * as comparableListingsActions from 'actionCreators/comparableListings';
import * as comparableRentalsActions from 'actionCreators/comparableRentals';
import * as comparableSalesActions from 'actionCreators/comparableSales';
import AddComparable from 'components/AddComparable';
import ComparableSortBy from 'components/ComparableSortBy';
import ComparablePropertyResults from 'components/ComparablePropertyResults';
import ComparableColumnsFooter from 'components/ComparableColumnsFooter';
import SelectedComparableColumnsHeader from 'components/SelectedComparableColumnsHeader';
import InformationTip from 'components/InformationTip';
import SelectedComparableListContainer from 'components/SelectedComparableListContainer';
import { ScrollToTop } from 'components/ScrollToTop';
import ComparableColumnsHeaderContainer from 'containers/ComparableColumnsHeaderContainer';
import AnalyticsGateway from 'lib/AnalyticsGateway';
import useFilterStore from 'zustandStore/useFilterStore';
import FiltersV2 from 'components/Filters/FiltersV2';
import TargetProperty from 'components/TargetProperty';
import * as Routes from '../Routes';
import { hasAdvancedFiltersEntitlement } from 'lib/entitlementsUtil';
import PROPERTY_TYPES from 'constants/propertyTypes';
import { getDateRangeFromMonths } from 'lib/SearchFilterUtil';
import {UPDATE} from 'ducks/filters';
import {COMPARABLE_TYPE} from 'constants/comparableTypes';
import { getCountryCode } from 'lib/PropertyUtils';

const typeActions = {
    sales: comparableSalesActions,
    listings: comparableListingsActions,
    rentals: comparableRentalsActions,
};

const typeSorts = {
    sales: [
        {text: 'Closest property', value: 'distance_asc'},
        {text: 'Address', value: 'address_asc'},
        {text: 'Sale date', value: 'date_desc'},
        {text: 'Lowest sale price', value: 'price_asc'},
        {text: 'Highest sale price', value: 'price_desc'},
    ],
    listings: [
        {text: 'Closest property', value: 'distance_asc'},
        {text: 'Address', value: 'address_asc'},
        {text: 'Lowest listing price', value: 'price_asc'},
        {text: 'Highest listing price', value: 'price_desc'},
    ],
    rentals: [
        {text: 'Closest property', value: 'distance_asc'},
        {text: 'Address', value: 'address_asc'},
        {text: 'Lowest Listing Price', value: 'price_asc'},
        {text: 'Highest Listing Price', value: 'price_desc'},
        {text: 'Newest Listing Date', value: 'date_desc'},
    ],
};

function ComparablesPage(props) {
    const {
        comparableType,
        featureFlags,
        match,
        propertyDetail,
        propertyNotes,
        settings,
        reportType,
        fetching,
        selectedComparablesFooter: SelectedComparablesFooter,
    } = props;

    const dispatch = useDispatch();
    const comparables = useSelector(state => {
        const {comparableSales, comparableListings, comparableRentals} = state;
        return {
            sales: comparableSales,
            listings: comparableListings,
            rentals: comparableRentals,
        }[comparableType];
    });

    const initializeFilters = useFilterStore(state => state.initializeFilters);
    const resetFilters = useFilterStore(state => state.resetFilters);
    const updateInitialFilters = useFilterStore(state => state.updateInitialFilters);
    const clearInitializationState = useFilterStore(state => state.clearInitializationState);
    const setAdvancedFilterRole = useFilterStore(state => state.setAdvancedFilterRole);
    const setIsEntitledForAdvancedFilters = useFilterStore(state => state.setIsEntitledForAdvancedFilters);
    const storedSubComparableType = useFilterStore(state => state.subComparableType);
    const setSubComparableType = useFilterStore(state => state.setSubComparableType);
    const propertyType = useFilterStore(state => state.filters[comparableType].propertyType);
    const { entitlements } = useSelector(state => state.userDetails);

    const [expanded, setExpanded] = useState(true);
    const [selectedExpanded, setSelectedExpanded] = useState(false);
    const prevPropertyIdRef = useRef(null);
    const hasInitializedRef = useRef(false);
    const soiDefaultsAppliedRef = useRef(false);

    const parsedPropertyId = useMemo(
        () => parseInt(match.params.propertyId, 10),
        [match.params.propertyId]
    );

    // Clear initialization state when propertyId changes
    useEffect(() => {
        if (prevPropertyIdRef.current !== null && prevPropertyIdRef.current !== parsedPropertyId) {
            clearInitializationState();
            hasInitializedRef.current = false;
            soiDefaultsAppliedRef.current = false;
        }
        prevPropertyIdRef.current = parsedPropertyId;
    }, [parsedPropertyId, clearInitializationState]);

    useEffect(() => {
        // Only send analytics and initialize filters once per property
        if (!hasInitializedRef.current) {
            AnalyticsGateway.sendEvent({
                eventName: `View Comparable ${capitalize(comparableType)}`,
                properties: [
                    {label: 'Page', value: `Comparable ${capitalize(comparableType)}`},
                    {label: 'Property ID', value: parsedPropertyId}
                ]
            });

            hasInitializedRef.current = true;
        }
    }, [comparableType, parsedPropertyId, initializeFilters]);

    useEffect(() => {
        setAdvancedFilterRole(hasAdvancedFiltersEntitlement(entitlements));
        setIsEntitledForAdvancedFilters(!!entitlements?.length);
    }, [entitlements, setAdvancedFilterRole, setIsEntitledForAdvancedFilters]);

    // Reset filters when transitioning between regular and soi comparable sales
    useEffect(() => {
        if (comparableType === 'sales' &&
            storedSubComparableType !== null &&
            storedSubComparableType !== props.subComparableType) {
            resetFilters(COMPARABLE_TYPE.SALES);
            soiDefaultsAppliedRef.current = false;
        }
        setSubComparableType(props.subComparableType);
    }, [props.subComparableType, comparableType, storedSubComparableType, resetFilters, setSubComparableType]);

    // setting initial filters
    useEffect(() => {
        if (parsedPropertyId && propertyDetail?.latitude && propertyDetail?.longitude) {
            const targetPropertyType = capitalize(propertyDetail?.propertyType);
            const applySOIDefaults = getCountryCode() === 'au' &&
                props.subComparableType === 'soi' &&
                !soiDefaultsAppliedRef.current;
            if (applySOIDefaults) soiDefaultsAppliedRef.current = true;
            const soiDefaultValues = propertyDetail?.metroProperty
                ? { distance: 2, date: getDateRangeFromMonths(6) }
                : { distance: 5, date: getDateRangeFromMonths(18) };
            updateInitialFilters({
                // only set property type if the target property type is in our defined PROPERTY_TYPES list
                ...(PROPERTY_TYPES.some(({ value }) => value === targetPropertyType) ? {propertyType: [targetPropertyType]} : {}),
                ...(applySOIDefaults ? soiDefaultValues : {}),
            }, parsedPropertyId);

            if (targetPropertyType && (targetPropertyType?.toLowerCase() !== propertyType?.toString().toLowerCase())) {
                dispatch({ type: UPDATE, payload: {} });
                dispatch(typeActions[comparableType].fetchItems(parsedPropertyId, true));
            }
        }
    }, [
        propertyDetail?.latitude,
        propertyDetail?.longitude,
        propertyDetail?.propertyType,
        propertyDetail?.metroProperty,
        parsedPropertyId,
        updateInitialFilters,
        props.subComparableType,
    ]);

    // Fetch on prop changes
    useEffect(() => {
        if (parsedPropertyId && propertyDetail?.latitude && propertyDetail?.longitude) {
            dispatch(typeActions[comparableType].fetchItems(parsedPropertyId));
        }
    }, [comparableType, propertyDetail?.latitude, propertyDetail?.longitude, parsedPropertyId]);

    // Analytics on unmount
    useEffect(() => {
        return () => {
            AnalyticsGateway.sendEvent({
                eventName: `Select Comparable ${capitalize(comparableType)}`,
                properties: [
                    {label: 'Page', value: `Comparable ${capitalize(comparableType)}`},
                    {label: 'Property ID', value: parsedPropertyId},
                    {label: 'Number of Properties Selected', value: comparables.selected.length},
                    {label: 'Number of Featured Comparables', value: comparables.featured.length},
                    {
                        label: 'Number of Notes Selected',
                        value: comparables.selected.filter(id => get(propertyNotes, [id, 'selected', comparableType])).length
                    }
                ]
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showTargetPropertySummary = useCallback(
        (pageContext) => ['Preview Report'].indexOf(pageContext) < 0,
        []
    );

    const onExpandCollapseAll = expanded => setExpanded(expanded);
    const onExpandCollapseAllSelected = expanded => setSelectedExpanded(expanded);

    const onAddComparable = comparable => {
        dispatch(typeActions[comparableType].fetchItem(comparable.propertyId, true));
        AnalyticsGateway.sendEvent({
            eventName: `Add Comparable ${capitalize(comparableType.replace(/s$/, ''))}`,
            properties: [
                {label: 'Page', value: `Comparable ${capitalize(comparableType)}`},
                {label: 'Property ID', value: comparable.propertyId}
            ]
        });
    };

    const onRemoveAllSelected = () => dispatch(typeActions[comparableType].updateSelectAll(false));
    const applyFilter = () => {
        dispatch(typeActions[comparableType].fetchItems(parsedPropertyId));
    };
    const onSortChange = (value) => {
        dispatch(typeActions[comparableType].updateSortBy(value, true));
        applyFilter();
    };
    const onLoadMoreClick = () => {
        dispatch(typeActions[comparableType].loadMore());
        applyFilter();
    };
    const {pageContext} = Routes.locationInfo(window.location.pathname);

    const comparableTypeText = capitalize(comparableType);
    const singularComparableType = comparableTypeText.slice(0, -1);

    return (
        <div className={`comparable-${comparableType}-page`}>
            <Container
                maxWidth={false}
                disableGutters
                sx={{
                    backgroundColor: '#fff',
                    boxShadow: '0 1px 0 0px #e0e0e0',
                    pt: 2.5,
                    pb: 2.5,
                }}
            >
                <FiltersV2
                    comparableType={comparableType}
                    applyFilter={applyFilter}
                    propertyId={parsedPropertyId}
                />
                <Container>
                    {showTargetPropertySummary(pageContext) && (
                        <TargetProperty
                            detail={propertyDetail}
                            hideOwnerNamesForVic={featureFlags.hideOwnerNamesForVic}
                            pageContext={pageContext}
                            reportType={reportType}
                            comparableType={comparableType}
                            showRadiusFilters
                        />
                    )}
                </Container>
                {props.children}
            </Container>
            <Container>
                <div className="section-top-bar">
                    <div className="selected-comparable-title">
                        <h3 className="selected-section-title">{`Selected Comparable ${comparableTypeText}`}</h3>
                        <InformationTip>
                            <div className="selected-comparable-tip-text">
                                Add comparables to your selected list from the search results below. You can re-arrange the
                                order of
                                your selected comparables using the re-order function on the left hand side of each
                                comparable in your
                                selected list.
                                <p>&nbsp;</p>
                                To feature a comparable ensure that you have checked the box in the ‘Featured’ column of
                                your selected
                                list.
                            </div>
                        </InformationTip>
                    </div>
                    <Form>
                        <Form.Group>
                            <Form.Field width={9}>
                                <Typography
                                    variant="fontWeight600"
                                    mb={0.5}
                                    sx={{ display: 'block' }}
                                >
                                    Add Comparable {singularComparableType}
                                </Typography>
                                <AddComparable disabled={fetching} onSelect={onAddComparable}/>
                            </Form.Field>
                        </Form.Group>
                    </Form>
                </div>
                <SelectedComparableColumnsHeader
                    comparablesType={comparableTypeText}
                    records={comparables.selected.length}
                    active={selectedExpanded}
                    onRemoveAll={onRemoveAllSelected}
                    onExpandCollapseAll={onExpandCollapseAllSelected}
                />
                <SelectedComparableListContainer active={selectedExpanded} comparableType={comparableType}/>
                {SelectedComparablesFooter && <SelectedComparablesFooter/>}
                <div className="section-top-bar">
                    <div className="search-result-title">
                        <h3 className="section-title">Search Results</h3>
                    </div>
                    <Form>
                        <Form.Group>
                            <ComparableSortBy
                                analyticsPage={`Comparable ${capitalize(comparableType)}`}
                                comparables={comparables}
                                disabled={fetching}
                                onChange={onSortChange}
                                options={typeSorts[comparableType]}
                            />
                        </Form.Group>
                    </Form>
                </div>
                <ComparableColumnsHeaderContainer
                    comparablesType={capitalize(comparableType).replace(/s$/, '')}
                    expanded={expanded}
                    onExpandCollapseAll={onExpandCollapseAll}
                    selectedCount={comparables.selected.length}
                />
                <ComparablePropertyResults
                    active={expanded}
                    comparables={comparables}
                    settings={settings}
                    featureFlags={featureFlags}
                />
                <ComparableColumnsFooter
                    comparables={comparables}
                    onLoadMoreClick={onLoadMoreClick}
                />
                <ScrollToTop/>
            </Container>
        </div>
    );
}

ComparablesPage.propTypes = {
    comparableType: PropTypes.oneOf(['sales', 'listings', 'rentals']).isRequired,
    comparableSales: PropTypes.object.isRequired,
    comparableListings: PropTypes.object.isRequired,
    comparableRentals: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    featureFlags: PropTypes.object,
    fetching: PropTypes.bool,
    match: PropTypes.object.isRequired,
    propertyDetail: PropTypes.object.isRequired,
    propertyNotes: PropTypes.object.isRequired,
    reportType: PropTypes.shape({value: PropTypes.string.isRequired}).isRequired,
    settings: PropTypes.object.isRequired,
    subComparableType: PropTypes.string,
    selectedComparablesFooter: PropTypes.elementType,
};

const mapStateToProps = state => ({
    featureFlags: state.featureFlags,
    propertyDetail: state.propertyDetail,
    propertyNotes: state.propertyNotes,
    reportType: state.reportType,
    settings: state.settings,
});

const MemoizedComparablesPage = memo(ComparablesPage, (prevProps, nextProps) => {
    return isEqual(prevProps, nextProps);
});

export default connect(mapStateToProps)(MemoizedComparablesPage);